/*eslint-disable*/
import { useState } from 'react';

// @mui/material components
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TablePagination from '@mui/material/TablePagination';
import { makeStyles } from '@mui/styles';

// material ui icons
import SearchResultsIcon from '@mui/icons-material/Reorder';
import SearchIcon from '@mui/icons-material/Search';
import ViewIcon from '@mui/icons-material/Visibility';

// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import Table from 'components/Table/Table.js';

import Device from './Device';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

// Redux
import {
  useGetDeviceDictionaryQuery,
  useGetLocationsQuery,
  useLazyGetDeviceQuery,
  useLazyGetWorkstationsQuery,
  useLazySearchDevicesQuery,
} from 'api/location.api';
import { getStackTrace, log } from '@src/Log.init';
import { useDispatch } from 'react-redux';
import { setCurrentDevice } from '../../features/location/locationSlice';

const useStyles = makeStyles(styles);

export default function SearchCriteria(props) {
  const dispatch = useDispatch();

  const { data: locationsData } = useGetLocationsQuery({ limit: 20 });
  const locations = locationsData?.results || [];
  const [triggerGetWorkstations, { data: workstations, isSuccess }] =
    useLazyGetWorkstationsQuery();
  const [triggerGetDevices, { data: devices, isSuccess: isDeviceSuccess }] =
    useLazySearchDevicesQuery();
  const resultCount = isDeviceSuccess && devices.metadata.total;
  const { data: deviceDictionary } = useGetDeviceDictionaryQuery();
  const [triggerGetDevice] = useLazyGetDeviceQuery();

  const classes = useStyles();

  const [dirtyData, setDirtyData] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDevice, setViewDevice] = useState(false);

  const [location, setLocation] = useState('');
  const [locationState, setLocationState] = useState('');
  const [workstation, setWorkstation] = useState('');
  const [workstationState, setWorkstationState] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [deviceTypeState, setDeviceTypeState] = useState('');
  const [model, setModel] = useState('');
  const [modelState, setModelState] = useState('');
  const [status, setStatus] = useState('');
  const [statusState, setStatusState] = useState('');
  const [shared, setShared] = useState('');
  const [sharedlState, setSharedState] = useState('');
  const [lastUsed, setLastUsed] = useState('');
  const [lastUsedState, setLastUsedState] = useState('');

  const onClickSearch = async () => {
    await searchDevices();
  };

  const searchDevices = async (pg, rows) => {
    const searchCriteria = {};
    if (location && location !== '') {
      searchCriteria.location = location;
      searchCriteria.location_id = location;
    }
    if (workstation && workstation !== '')
      searchCriteria.workstation_id = workstation;
    if (deviceType && deviceType !== '') searchCriteria.type = deviceType;
    if (model && model !== '') searchCriteria.model = model;
    if (status && status !== '') searchCriteria.status = status;
    if (shared !== '') searchCriteria.shared = shared;
    if (lastUsed && lastUsed !== '') searchCriteria.lastUsed = lastUsed;
    searchCriteria.rowsPerPage = rows ? rows : rowsPerPage;
    searchCriteria.page = pg ? pg : page;
    await triggerGetDevices(searchCriteria).unwrap();
    setDirtyData(false);
  };

  const validCriteria = () => {
    return (
      location !== '' ||
      workstation !== '' ||
      deviceType !== '' ||
      model !== '' ||
      status !== '' ||
      shared !== '' ||
      lastUsed !== ''
    );
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage + 1);
    setDirtyData(true);
    await searchDevices(newPage + 1);
  };

  const handleChangeRowsPerPage = async (event) => {
    const rowsPP = parseInt(event.target.value, 10);
    setRowsPerPage(rowsPP);
    setPage(1);
    setDirtyData(true);
    await searchDevices(1, rowsPP);
  };

  const locationList = () => {
    const locs = locations
      .filter((loc) => loc.status === 'active')
      .map((location, key) => {
        const option = (
          <MenuItem key={key} value={location.key}>
            {location.key + ', ' + location.name}
          </MenuItem>
        );
        return option;
      });
    locs.unshift(
      <MenuItem key={-1} value=''>
        -Empty-
      </MenuItem>
    );
    return locs;
  };

  const workstationList = () => {
    if (!isSuccess || !workstations) return [];
    const locs = workstations
      .filter((wrkstn) => wrkstn.status === 'Active')
      .map((workstation, key) => {
        const option = (
          <MenuItem key={key} value={workstation.workstation_id}>
            {workstation.workstation_id + ', ' + workstation.description}
          </MenuItem>
        );
        return option;
      });
    locs.unshift(
      <MenuItem key={-1} value=''>
        -Empty-
      </MenuItem>
    );
    return locs;
  };

  const deviceDictionaryList = () => {
    const locs = deviceDictionary
      .filter((entry) => entry.type === deviceType)
      .map((deviceEntry, key) => {
        const option = (
          <MenuItem key={key} value={deviceEntry.name}>
            {deviceEntry.name}
          </MenuItem>
        );
        return option;
      });
    locs.unshift(
      <MenuItem key={-1} value=''>
        -Empty-
      </MenuItem>
    );
    return locs;
  };

  const locationChangeHandler = (event) => {
    if (event.target.value !== location) {
      if (event.target.value !== '') {
        triggerGetWorkstations(event.target.value)
          .unwrap()
          .catch((error) =>
            log.error({
              message_type: 'SystemError',
              message: `Error retrieving workstations for location ${event.target.value}: ${error.message}`,
              meta_data: getStackTrace(),
            })
          );
      }
      setWorkstation(''); // any value for workstation is tied to the previous location
      setLocation(event.target.value);
      setLocationState('success');
      setDirtyData(true);
    }
  };
  const workstationChangeHandler = (event) => {
    if (event.target.value !== workstation) {
      setWorkstation(event.target.value);
      setWorkstationState('success');
      setDirtyData(true);
    }
  };
  const typeChangeHandler = (event) => {
    if (event.target.value !== deviceType) {
      setDeviceType(event.target.value);
      setDeviceTypeState('success');
      setDirtyData(true);
    }
  };
  const modelChangeHandler = (event) => {
    if (event.target.value !== model) {
      setModel(event.target.value);
      setModelState('success');
      setDirtyData(true);
    }
  };
  const statusChangeHandler = (event) => {
    if (event.target.value !== status) {
      setStatus(event.target.value);
      setStatusState('success');
      setDirtyData(true);
    }
  };
  const sharedChangeHandler = (event) => {
    if (event.target.value !== shared) {
      setShared(event.target.value);
      setSharedState('success');
      setDirtyData(true);
    }
  };
  const lastUsedChangeHandler = (event) => {
    if (event.target.value !== lastUsed) {
      setLastUsed(event.target.value);
      setLastUsedState('success');
      setDirtyData(true);
    }
  };
  const handleViewDevice = (index) => {
    if (!isDeviceSuccess || !devices || devices.data.length === 0) {
      return;
    }
    triggerGetDevice(devices.data[index]._id)
      .unwrap()
      .then((res) => {
        dispatch(setCurrentDevice(res));
        setViewDevice(true);
      });
  };

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };
  const searchResults = () => {
    let results = [];
    if (devices.data.length > 0) {
      results = devices.data.map((device, key) => {
        let result = [];
        let deviceId =
          device.type === 'Workstation'
            ? device.workstation_id
            : device.type === 'Cashdrawer' || device.type === 'Safe'
            ? device.cashdrawer_id
            : '';

        let actionButtons = [];
        result.push(device.type);
        result.push(device.location);
        result.push(device.name);
        result.push(deviceId);
        result.push(device.description);
        result.push(device.status);
        result.push(
          device.last_used
            ? new Date(device.last_used).toLocaleString()
            : 'Unused'
        );
        actionButtons.push(
          actionButton(ViewIcon, 'warning', handleViewDevice, key)
        );
        result.push(actionButtons);
        return result;
      });
    }
    return results;
  };

  const handleCloseDevice = () => {
    setViewDevice(false);
  };

  const handleCloseAndRefresh = () => {
    setViewDevice(false);
    searchDevices();
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <SearchIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Search Criteria</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={4}>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='location-select-label'
                    className={classes.inputLabel}
                  >
                    Location
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='location-select-label'
                    id='location-select'
                    value={location}
                    onChange={(event) => locationChangeHandler(event)}
                  >
                    {locationList()}
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='workstation-select-label'
                    className={classes.inputLabel}
                  >
                    Workstation
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='workstation-select-label'
                    id='workstation-select'
                    value={workstation}
                    onChange={(event) => workstationChangeHandler(event)}
                  >
                    {workstationList()}
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='lastused-select-label'
                    className={classes.inputLabel}
                  >
                    Last Used
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='lastused-select-label'
                    id='lastused-select'
                    value={lastUsed}
                    onChange={(event) => lastUsedChangeHandler(event)}
                  >
                    <MenuItem key={-1} value=''>
                      -Empty-
                    </MenuItem>
                    <MenuItem value='1'>More than 1 Month Ago</MenuItem>
                    <MenuItem value='3'>More than 3 Months Ago</MenuItem>
                    <MenuItem value='6'>More than 6 Months Ago</MenuItem>
                    <MenuItem value='12'>More than 1 Year Ago</MenuItem>
                    <MenuItem value='-1'>Never</MenuItem>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={4}>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='type-select-label'
                    className={classes.inputLabel}
                  >
                    Device Type
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='type-select-label'
                    id='type-select'
                    value={deviceType}
                    onChange={(event) => typeChangeHandler(event)}
                  >
                    <MenuItem key={-1} value=''>
                      -Empty-
                    </MenuItem>
                    <MenuItem value='Printer'>Printer</MenuItem>
                    <MenuItem value='Cashdrawer'>Cash Drawer</MenuItem>
                    <MenuItem value='Scanner'>Scanner</MenuItem>
                    <MenuItem value='PED'>PED</MenuItem>
                    <MenuItem value='Workstation'>Workstation</MenuItem>
                    <MenuItem value='Safe'>Safe</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='model-select-label'
                    className={classes.inputLabel}
                  >
                    Device Model
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='model-select-label'
                    id='model-select'
                    value={model}
                    onChange={(event) => modelChangeHandler(event)}
                  >
                    {deviceDictionaryList()}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={4}>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='status-select-label'
                    className={classes.inputLabel}
                  >
                    Device Status
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='status-select-label'
                    id='status-select'
                    value={status}
                    onChange={(event) => statusChangeHandler(event)}
                  >
                    <MenuItem key={-1} value=''>
                      -Empty-
                    </MenuItem>
                    <MenuItem value='Active'>Active</MenuItem>
                    <MenuItem value='Inactive'>Inactive</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='shared-select-label'
                    className={classes.inputLabel}
                  >
                    Shared?
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='shared-select-label'
                    id='shared-select'
                    value={shared}
                    onChange={(event) => sharedChangeHandler(event)}
                  >
                    <MenuItem key={-1} value=''>
                      -Empty-
                    </MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12}>
                <Button
                  color='rose'
                  onClick={onClickSearch}
                  disabled={!dirtyData || !validCriteria()}
                  className={classes.registerButton}
                >
                  Search
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      {isDeviceSuccess && devices.data.length > 0 ? (
        <GridItem xs={12}>
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <SearchResultsIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Devices</h4>
            </CardHeader>
            <CardBody>
              <Table
                tableHead={[
                  'Type',
                  'Location',
                  'Model',
                  'ID',
                  'Description',
                  'Status',
                  'Last Used',
                  'Actions',
                ]}
                tableData={searchResults()}
              />
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={resultCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
              />
            </CardBody>
          </Card>
        </GridItem>
      ) : null}
      {viewDevice === true && isDeviceSuccess ? (
        <Device
          open={viewDevice}
          handleClose={handleCloseDevice}
          handleCloseAndRefresh={handleCloseAndRefresh}
        />
      ) : null}
    </GridContainer>
  );
}
